import { api } from './api';

export interface LinkedInAccountOwner {
  id: number;
  name: string;
}

export interface LinkedInAccount {
  id: number;
  name: string;
  created_on: string | null;      // ISO date the LinkedIn account was created (age anchor)
  start_connections: number;
  current_connections: number;
  notes: string | null;
  archived: number;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  // Geteiltes Login (Team-sichtbar): E-Mail + entschlüsseltes Passwort (server-seitig Fernet-verschlüsselt gespeichert)
  login_email: string | null;
  login_password: string | null;
  // owners (m:n with web_users) = Operatoren (wer darf senden)
  owners: LinkedInAccountOwner[];
  owner_ids: number[];
  // Inhaber (Identität): die eine Person, auf deren Namen alle Leads dieses Accounts laufen
  owner_web_user_id: number | null;
  owner_name: string | null;
  // FR-Tageslimit NUR dieses Accounts; null = das globale fr_daily_limit gilt
  daily_fr_limit: number | null;
  // derived warm-up fields (server-computed)
  age_days: number | null;
  connections_gained: number;
  is_warming_up: boolean;
  warmup_days: number;
  warmup_connections_target: number;
  normal_daily_limit: number;
  effective_daily_limit: number;
  // Posting-Persona-Metadaten: dieselbe reale Person, die per diesem Account anschreibt, postet
  // oft auch. Live von Content Studio gelesen (services/content_ai.py:_resolve_persona_account).
  author_role: string | null;
  author_proof_points: string | null;
  author_voice_samples: string | null;
  expertise_level: LinkedInExpertiseLevel | null;
  // Nr. 95/96/98: Winkel (welche Strategy-Pillars dieses Profil bedienen darf) + Markt (welche
  // Länder es bearbeitet) — roh als JSON-Text vom Server, NULL/leer = kein Riegel. Geparst wird
  // an der Verbrauchsstelle (siehe toForm() in LinkedInAccountsPage.tsx).
  content_pillars_json: string | null;
  markt_json: string | null;
}

export type LinkedInExpertiseLevel = 'einsteiger' | 'gemischt' | 'fachpublikum';

// Schlanke Content-Studio-Sicht (GET /linkedin-accounts/personas) — NIE Login-/Warm-up-/
// Limit-Felder, siehe db_web/linkedin_accounts.py:get_linkedin_account_personas.
export interface LinkedInAccountPersona {
  id: number;
  name: string;
  author_role: string | null;
  author_proof_points: string | null;
  author_voice_samples: string | null;
  expertise_level: LinkedInExpertiseLevel | null;
}

export interface LinkedInAccountInput {
  name?: string;
  created_on?: string | null;
  start_connections?: number;
  current_connections?: number;
  notes?: string | null;
  archived?: number;
  owner_ids?: number[];
  owner_web_user_id?: number | null;
  login_email?: string | null;
  login_password?: string | null;
  daily_fr_limit?: number | null;  // leer/0 = globales Limit
  author_role?: string | null;
  author_proof_points?: string | null;
  author_voice_samples?: string | null;
  expertise_level?: LinkedInExpertiseLevel | null;
  // Nr. 95/96/98: Arrays, nicht JSON-Text -- der Server serialisiert. Leeres Array MUSS mitgesendet
  // werden, um das Feld zurückzusetzen (der Server überspringt `undefined`/fehlende Keys beim PATCH).
  content_pillars?: string[];
  markt?: string[];
}

export interface LinkedInAccountPillars {
  verknuepft: boolean;
  pillars: { name: string; description: string | null }[];
}

export const linkedinAccountsApi = {
  list: (includeArchived = false) =>
    api.get<LinkedInAccount[]>(`/linkedin-accounts${includeArchived ? '?include_archived=true' : ''}`),

  // Content-Studio-Picker: nie den vollen list()-Response verwenden — der trägt entschlüsselte
  // Klartext-Login-Passwörter, die Content Studio nie braucht (siehe LinkedInAccountPersona).
  listPersonas: (includeArchived = false) =>
    api.get<LinkedInAccountPersona[]>(
      `/linkedin-accounts/personas${includeArchived ? '?include_archived=true' : ''}`
    ),

  create: (data: LinkedInAccountInput) => api.post<LinkedInAccount>('/linkedin-accounts', data),

  update: (id: number, data: LinkedInAccountInput) =>
    api.patch<LinkedInAccount>(`/linkedin-accounts/${id}`, data),

  remove: (id: number) => api.delete<{ ok: boolean }>(`/linkedin-accounts/${id}`),

  // Nr. 95: welche Content-Pillars die verknüpfte Strategy pflegt — es gibt keine direkte Spalte,
  // die Verknüpfung läuft nur in der Gegenrichtung (Strategy → Account).
  pillars: (id: number) => api.get<LinkedInAccountPillars>(`/linkedin-accounts/${id}/pillars`),
};
